import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

const STORAGE_KEY = "request-drawer-width";
const MIN_WIDTH = 400;
const MD_BREAKPOINT = 768;

/**
 * Controller for managing the request details drawer.
 * Handles opening/closing the drawer, ESC key, click-outside behavior,
 * and drag-to-resize with localStorage persistence.
 */
export default class extends Controller {
  static targets = ["toggle", "frame", "loading", "panel", "row", "resizeHandle"];
  static values = { open: String };

  declare readonly toggleTarget: HTMLInputElement;
  declare readonly frameTarget: HTMLElement;
  declare readonly loadingTarget: HTMLElement;
  declare readonly panelTarget: HTMLElement;
  declare readonly resizeHandleTarget: HTMLElement;
  declare readonly rowTargets: HTMLElement[];

  declare readonly hasToggleTarget: boolean;
  declare readonly hasFrameTarget: boolean;
  declare readonly hasLoadingTarget: boolean;
  declare readonly hasPanelTarget: boolean;
  declare readonly hasResizeHandleTarget: boolean;
  declare readonly hasRowTarget: boolean;

  declare openValue: string;
  declare readonly hasOpenValue: boolean;

  private boundHandleKeydown: ((event: KeyboardEvent) => void) | null = null;
  private boundHandleFrameLoad: (() => void) | null = null;
  private boundMouseMove: ((event: MouseEvent) => void) | null = null;
  private boundMouseUp: ((event: MouseEvent) => void) | null = null;
  private activeRowId: string | null = null;
  private rafId: number | null = null;

  connect(): void {
    // Bind handlers
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);

    // Listen for ESC key globally
    document.addEventListener("keydown", this.boundHandleKeydown);

    // Listen for Turbo Frame load events
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener(
        "turbo:frame-load",
        this.boundHandleFrameLoad
      );
    }

    // Restore saved width on desktop
    this.restoreSavedWidth();

    // Auto-open a request if specified via value
    if (this.hasOpenValue && this.openValue) {
      requestAnimationFrame(() => {
        const row = this.rowTargets.find(
          (r) => r.dataset.requestId === this.openValue
        );
        if (row) {
          this.openRow({ currentTarget: row } as unknown as Event);
        }
      });
    }
  }

  disconnect(): void {
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown);
    }

    if (this.hasFrameTarget && this.boundHandleFrameLoad) {
      this.frameTarget.removeEventListener(
        "turbo:frame-load",
        this.boundHandleFrameLoad
      );
    }

    // Clean up any lingering resize listeners
    this.stopResize();
  }

  /**
   * Starts a resize drag operation on mousedown of the resize handle.
   */
  startResize(event: MouseEvent): void {
    event.preventDefault();

    document.addEventListener("mousemove", this.boundMouseMove!);
    document.addEventListener("mouseup", this.boundMouseUp!);

    // Prevent text selection while dragging
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }

  private handleMouseMove(event: MouseEvent): void {
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;

      if (!this.hasPanelTarget) return;

      const maxWidth = Math.floor(window.innerWidth * 0.8);
      const newWidth = Math.min(
        Math.max(window.innerWidth - event.clientX, MIN_WIDTH),
        maxWidth
      );

      this.panelTarget.style.width = `${newWidth}px`;
    });
  }

  private handleMouseUp(_event: MouseEvent): void {
    if (this.hasPanelTarget) {
      const currentWidth = this.panelTarget.style.width;
      if (currentWidth) {
        localStorage.setItem(STORAGE_KEY, currentWidth);
      }
    }

    this.stopResize();
  }

  private stopResize(): void {
    if (this.boundMouseMove) {
      document.removeEventListener("mousemove", this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener("mouseup", this.boundMouseUp);
    }

    document.body.style.userSelect = "";
    document.body.style.cursor = "";

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private restoreSavedWidth(): void {
    // Only apply on desktop (md breakpoint and above)
    if (window.innerWidth < MD_BREAKPOINT) return;
    if (!this.hasPanelTarget) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    const parsed = parseInt(saved, 10);
    if (isNaN(parsed)) return;

    const maxWidth = Math.floor(window.innerWidth * 0.8);
    const clamped = Math.min(Math.max(parsed, MIN_WIDTH), maxWidth);
    this.panelTarget.style.width = `${clamped}px`;
  }

  /**
   * Opens the drawer when a row is clicked.
   * Loads the request details via Turbo Frame.
   */
  openRow(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const url = target.dataset.requestUrl;
    const requestId = target.dataset.requestId;

    if (!url) return;

    // Update active row highlighting
    this.setActiveRow(requestId || null);

    // Open the drawer
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = true;
    }

    // Show loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }

    // Load content via Turbo Frame
    if (this.hasFrameTarget) {
      Turbo.visit(url, { frame: "request-details" });
    }

    try {
      window.umami?.track("request-drawer-open");
    } catch {
      // no-op
    }
  }

  /**
   * Opens an image lightbox dialog and tracks the event.
   * Wired via `data-action="click->request-drawer#openLightbox"` with
   * `data-lightbox-id="..."` on the trigger.
   */
  openLightbox(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const id = target.dataset.lightboxId;
    if (!id) return;

    const dialog = document.getElementById(id) as HTMLDialogElement | null;
    if (!dialog) return;

    dialog.showModal();

    try {
      window.umami?.track("request-lightbox-open");
    } catch {
      // no-op
    }
  }

  /**
   * Tracks a request-payloads-purge event.
   * Wired via `data-action="turbo:submit-start->request-drawer#trackPurge"`
   * on the purge form.
   */
  trackPurge(): void {
    try {
      window.umami?.track("request-payloads-purged");
    } catch {
      // no-op
    }
  }

  /**
   * Opens the drawer by checking the toggle checkbox.
   * Called when a details button is clicked.
   */
  open(_event: Event): void {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = true;
    }

    // Show loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }
  }

  /**
   * Closes the drawer by unchecking the toggle checkbox.
   * Called when the overlay is clicked, ESC is pressed, or close button is clicked.
   */
  close(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (this.hasToggleTarget) {
      this.toggleTarget.checked = false;
    }

    // Clear active row highlighting
    this.setActiveRow(null);
  }

  /**
   * Sets the active row and updates highlighting.
   */
  private setActiveRow(requestId: string | null): void {
    // Remove highlight from previous active row
    this.rowTargets.forEach((row) => {
      row.classList.remove("bg-base-200");
    });

    this.activeRowId = requestId;

    // Add highlight to new active row
    if (requestId) {
      const activeRow = this.rowTargets.find(
        (row) => row.dataset.requestId === requestId
      );
      if (activeRow) {
        activeRow.classList.add("bg-base-200");
      }
    }
  }

  /**
   * Handles keydown events - closes drawer on ESC.
   */
  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.isOpen()) {
      // Don't close drawer if a dialog (lightbox) is open — let it close first
      const openDialog = document.querySelector("dialog[open]");
      if (openDialog) return;

      event.preventDefault();
      this.close();
    }
  }

  /**
   * Handles successful Turbo Frame load.
   * Hides the loading skeleton when content arrives.
   */
  private handleFrameLoad(): void {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }
  }

  /**
   * Checks if the drawer is currently open.
   */
  private isOpen(): boolean {
    return this.hasToggleTarget && this.toggleTarget.checked;
  }
}
